/**
 * Global Error Toast Service
 *
 * Singleton service for showing error toasts from anywhere in the application
 * including API interceptors (which are outside React component tree)
 */

import { ApiError } from "../types/errors";
import { ErrorHandlerService } from "./error-handler.service";

type ShowErrorFunction = (
  error: ApiError | string,
  options?: { onRetry?: () => void },
) => void;

class GlobalErrorToastService {
  private showErrorFn: ShowErrorFunction | null = null;

  /**
   * Initialize the service with the toast context function
   * This should be called from a React component that has access to the toast context
   */
  initialize(showErrorFn: ShowErrorFunction): void {
    this.showErrorFn = showErrorFn;
  }

  /**
   * Show error toast
   * Can be called from anywhere, including API interceptors
   */
  showError(
    error: unknown,
    options?: { onRetry?: () => void; silent?: boolean },
  ) {
    if (options?.silent || !this.showErrorFn) {
      return;
    }

    // Convert to ApiError if needed
    let apiError: ApiError;

    if (error instanceof Error && "type" in error && "retryable" in error) {
      // Already an ApiError
      apiError = error as ApiError;
    } else {
      // Convert to ApiError using ErrorHandlerService
      apiError = ErrorHandlerService.handleError(error);
    }

    this.showErrorFn(apiError, { onRetry: options?.onRetry });
  }

  /**
   * Check if service is initialized
   */
  isInitialized(): boolean {
    return this.showErrorFn !== null;
  }
}

// Export singleton instance
export const globalErrorToastService = new GlobalErrorToastService();
